/**
 * LUXDialog: React 19 호환 자체 다이얼로그 시스템
 *
 * CDN sena.css (SNA_dialog* 클래스) 기반 마크업 구조 사용.
 * luna-rocket LUXDialog API 호환 (alert, confirm).
 *
 * 사용법:
 *   LUXDialog.alert('메시지');
 *   LUXDialog.alert('메시지', { type: 'warning', title: '제목', confirmCallback: () => {} });
 *   LUXDialog.confirm('확인?', { type: 'warning', confirmCallback: () => {}, cancelCallback: () => {} });
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { translateNode, translateText } from '@/i18n';

// ── 타입 ────────────────────────────────────────────────────────────────────

type DialogIconType = 'success' | 'warning' | 'error' | 'info' | 'question';

export interface LUXDialogOptions {
  type?: DialogIconType | null;
  title?: string | null;
  /** luna-rocket 호환: alert 버튼 라벨 */
  buttonLabel?: string;
  /** luna-rocket 호환: confirm callback */
  callback?: () => void;
  callbackError?: () => void;
  callbackClose?: () => void;
  /** luna-rocket 호환: confirm 버튼 라벨 */
  submitLabel?: string;
  cancelLabel?: string;
  useRenewalStyle?: boolean;
  isVisibleCloseButton?: boolean;
  /** wehago-conference2 확장 (기존 래퍼 호환) */
  confirmCallback?: () => void;
  cancelCallback?: () => void;
  onCloseCallback?: () => void;
  confirmButtonLabel?: string | null;
  cancelButtonLabel?: string | null;
}

interface DialogState {
  open: boolean;
  mode: 'alert' | 'confirm';
  type: DialogIconType | null;
  title: string | null;
  content: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  confirmCallback: () => void;
  cancelCallback: () => void;
  closeCallback: () => void;
}

const INITIAL_STATE: DialogState = {
  open: false,
  mode: 'alert',
  type: null,
  title: null,
  content: null,
  confirmLabel: '확인',
  cancelLabel: '취소',
  confirmCallback: () => {},
  cancelCallback: () => {},
  closeCallback: () => {},
};

// ── 아이콘 type → CSS class 매핑 (CDN sena.css 기반) ────────────────────────

const ICON_CLASS_MAP: Record<DialogIconType, string> = {
  success: 'checkIcon',
  warning: 'warningIcon',
  error: 'errorIcon',
  info: 'infoIcon',
  question: 'questionIcon',
};

// ── 싱글턴 컨텍스트 ────────────────────────────────────────────────────────

type SetStateFn = (s: DialogState) => void;

let _setState: SetStateFn | null = null;

function closeDialog() {
  _setState?.({ ...INITIAL_STATE });
}

/** 문자열 내 \n → <br/>, 배열 → <br/> 구분 */
function normalizeMessage(msg: React.ReactNode): React.ReactNode {
  if (typeof msg === 'string') {
    const translated = translateText(msg);
    const parts = translated.split('\n');
    if (parts.length === 1) return translated;
    return parts.map((line, i) => (
      <React.Fragment key={i}>{line}{i < parts.length - 1 && <br />}</React.Fragment>
    ));
  }
  if (Array.isArray(msg)) {
    return msg.map((item, i) => (
      <React.Fragment key={i}>{translateNode(item)}{i < msg.length - 1 && <br />}</React.Fragment>
    ));
  }
  return translateNode(msg);
}

// ── Static API (luna-rocket LUXDialog 호환) ─────────────────────────────────

class LUXDialog {
  /** @internal — DialogRenderer에서 등록 */
  static _register(setter: SetStateFn) {
    _setState = setter;
  }

  static _unregister() {
    _setState = null;
  }

  /** luna-rocket 호환 systemContext (기존 코드 에러 방지용) */
  static systemContext: unknown = null;

  static alert(message: React.ReactNode, option?: LUXDialogOptions) {
    if (!_setState) {
      console.error('LUXDialog: DialogProvider가 마운트되지 않았습니다.');
      return;
    }
    const o = option || {};
    _setState({
      open: true,
      mode: 'alert',
      type: o.type || null,
      title: o.title ? translateText(o.title) : null,
      content: normalizeMessage(message),
      confirmLabel: translateText(o.buttonLabel || o.confirmButtonLabel || '확인'),
      cancelLabel: translateText('취소'),
      confirmCallback: o.callback || o.confirmCallback || (() => {}),
      cancelCallback: () => {},
      closeCallback: o.callbackClose || o.onCloseCallback || (() => {}),
    });
  }

  static confirm(message: React.ReactNode, option?: LUXDialogOptions) {
    if (!_setState) {
      console.error('LUXDialog: DialogProvider가 마운트되지 않았습니다.');
      return;
    }
    const o = option || {};
    _setState({
      open: true,
      mode: 'confirm',
      type: o.type || null,
      title: o.title ? translateText(o.title) : null,
      content: normalizeMessage(message),
      confirmLabel: translateText(o.submitLabel || o.confirmButtonLabel || '확인'),
      cancelLabel: translateText(o.cancelLabel || o.cancelButtonLabel || '취소'),
      confirmCallback: o.callback || o.confirmCallback || (() => {}),
      cancelCallback: o.callbackError || o.cancelCallback || (() => {}),
      closeCallback: o.callbackClose || o.onCloseCallback || (() => {}),
    });
  }
}

// ── 다이얼로그 렌더러 (senaalert.html 마크업 구조 동일) ─────────────────────

const DialogRenderer: React.FC = () => {
  const [state, setState] = React.useState<DialogState>({ ...INITIAL_STATE });
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    LUXDialog._register(setState);
    return () => LUXDialog._unregister();
  }, []);

  // 열릴 때 확인 버튼 포커스 + 스크롤 잠금
  useEffect(() => {
    if (state.open) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => confirmBtnRef.current?.focus(), 0);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [state.open]);

  const handleClose = useCallback(() => {
    state.closeCallback();
    closeDialog();
  }, [state]);

  const handleConfirm = useCallback(() => {
    state.confirmCallback();
    closeDialog();
  }, [state]);

  const handleCancel = useCallback(() => {
    state.cancelCallback();
    closeDialog();
  }, [state]);

  // 키보드: Escape → 닫기, Enter → 확인
  useEffect(() => {
    if (!state.open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'Enter') {
        handleConfirm();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state.open, handleClose, handleConfirm]);

  if (!state.open) return null;

  const iconClass = state.type ? ICON_CLASS_MAP[state.type] : null;
  const hasTitle = state.title != null && state.title !== '';

  return (
    <div className="SNA_dialogWrap rtcCustoms_luxDialogWrap">
      <div className="SNA_dialogDimmed" onClick={handleClose} />
      <div className="SNA_dialogBox rtcCustoms_luxDialogBox">
        {/* 닫기 버튼 */}
        <button type="button" className="SNA_dialogCloseBtn" onClick={handleClose}>
          <span className="SNA_dialogCloseBtnIcon">
            <span className="lunacon lunacon--close-outlined" />
          </span>
          <span className="blind">닫기</span>
        </button>

        {/* 아이콘 */}
        {iconClass && (
          <div className="SNA_dialogAlertIcon">
            <span className={`SNA_dialogAlertIconData ${iconClass}`} />
          </div>
        )}

        {/* 콘텐츠 */}
        <div className="SNA_dialogContent">
          {hasTitle && <h3 className="SNA_dialogMainTitle">{state.title}</h3>}
          <p className={`SNA_dialogText${hasTitle ? '' : ' fontMD'}`}>{state.content}</p>
        </div>

        {/* 버튼 영역 */}
        <div className="SNA_dialogUnderBtnBox">
          {state.mode === 'confirm' && (
            <button type="button" className="SNA_dialogUnderBtn" onClick={handleCancel}>
              <span className="SNA_dialogUnderBtnText">{state.cancelLabel}</span>
            </button>
          )}
          <button
            ref={confirmBtnRef}
            type="button"
            className="SNA_dialogUnderBtn confirmBtn"
            onClick={handleConfirm}
          >
            <span className="SNA_dialogUnderBtnText">{state.confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export { DialogRenderer };
export default LUXDialog;
